import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import _ from 'lodash'
import { observer } from 'mobx-react'
import { useContext, useRef, useState } from 'react'
import { useParams } from 'react-router'

import { ConfirmButton } from '@/components/ConfirmButton'
import { DataTable } from '@/components/DataTable'
import { SortableColumnHeader } from '@/components/DataTable/DataTable'
import { Badge } from '@/components/elements/badge'
import { Button } from '@/components/elements/button'
import { Input } from '@/components/elements/input'
import { Layout } from '@/components/Layout'
import {
  deleteExternalCertificationCategory,
  getExternalCertificationCategories,
} from '@/services/external_certifications.service'
import { UserStoreContext } from '@/stores/UserStore'
import { cn, toast } from '@/utils/helpers'

import AddEditCategoryModal from './AddEditCategoryModal'

const CertificationCategories = observer(() => {
  const { entityId } = useParams()
  const { isEntityAdmin, isEntityOwner, isXverseAdmin } = useContext(UserStoreContext)
  const canManage = isXverseAdmin || isEntityAdmin || isEntityOwner

  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [sorting, setSorting] = useState([{ id: 'name', desc: false }])
  const [pagination, setPagination] = useState({
    pageSize: 20,
    pageIndex: 0,
  })
  const [showModal, setShowModal] = useState(false)
  const [editCategory, setEditCategory] = useState(null)

  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()

  const { data: categories, isPending } = useQuery({
    queryKey: ['external-certification-categories', entityId, sorting, pagination, searchTerm],
    queryFn: () =>
      getExternalCertificationCategories({
        entityId,
        sorting,
        pagination,
        searchTerm: searchTerm || undefined,
      }),
    placeholderData: keepPreviousData,
  })

  const { isPending: pendingDelete, mutate: deleteCategory } = useMutation({
    mutationFn: (data) => deleteExternalCertificationCategory(entityId, data.id),
    onError: (err) => handleError(err.message),
    onSuccess: () => {
      handleSuccess('Category deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['external-certification-categories', entityId] })
    },
  })

  // Search shrinks the result set, so a deep page can 404 from the paginator.
  // Reset to page 0 in the same handler so React batches the state updates.
  const resetToFirstPage = () =>
    setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }))

  const debounceSearch = useRef(
    _.debounce((value) => {
      setSearchTerm(value)
      resetToFirstPage()
    }, 500),
  ).current

  // Filter to only show entity-specific categories (not global ones)
  // Coerce both to strings to handle numeric vs string IDs
  const normalizedEntityId = entityId != null ? String(entityId) : undefined
  const entityCategories = (categories?.results || []).filter(
    (cat) =>
      (cat.entity != null && String(cat.entity) === normalizedEntityId) ||
      (cat.entity?.id != null && String(cat.entity.id) === normalizedEntityId),
  )

  return (
    <Layout
      breadcrumbs={[
        {
          title: 'Documents',
          pathname: `/accounts/${entityId}/documents?tab=account-certifications`,
        },
        'External Credential Categories',
      ]}
    >
      <Layout.Heading title="External Credential Categories" />

      <div className="flex flex-row items-center gap-2">
        {searchTerm && (
          <Button
            className="visible sm:hidden"
            variant="outline"
            onClick={() => {
              setSearch('')
              setSearchTerm('')
              resetToFirstPage()
            }}
          >
            Clear Filters
          </Button>
        )}
        <Button
          className="visible sm:hidden"
          disabled={!canManage}
          onClick={() => setShowModal(true)}
        >
          Add Category
        </Button>
      </div>

      <div className="flex w-full flex-row items-center justify-between">
        <div className="mt-2 mb-2 flex w-full flex-col items-center gap-4 py-1 sm:flex-row">
          <Input
            className="w-full sm:w-64"
            placeholder="Search for Category"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              debounceSearch(e.target.value)
            }}
          />

          {searchTerm && (
            <Button
              className="hidden sm:block"
              variant="outline"
              onClick={() => {
                setSearch('')
                setSearchTerm('')
                resetToFirstPage()
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>

        <Button
          className="hidden sm:block"
          disabled={!canManage}
          onClick={() => setShowModal(true)}
        >
          Add Category
        </Button>
      </div>

      <DataTable
        columns={[
          {
            accessorKey: 'name',
            header: ({ column }) => <SortableColumnHeader column={column} label="Name" />,
            cell: ({ row }) => (
              <button
                type="button"
                className="text-action cursor-pointer text-left hover:underline"
                disabled={!canManage}
                onClick={() => {
                  setEditCategory(row.original)
                  setShowModal(true)
                }}
              >
                {row.original.name}
              </button>
            ),
          },
          {
            accessorKey: 'description',
            header: ({ column }) => <SortableColumnHeader column={column} label="Description" />,
            cell: ({ cell }) => (
              <span
                className={cn(
                  'max-w-[200px] truncate text-ellipsis',
                  !cell.getValue() && 'text-muted-foreground',
                )}
              >
                {cell.getValue() || 'No description'}
              </span>
            ),
          },
          {
            accessorKey: 'isActive',
            header: 'Status',
            cell: ({ cell }) => (
              <Badge variant={cell.getValue() ? 'success' : 'secondary'}>
                {cell.getValue() ? 'Active' : 'Inactive'}
              </Badge>
            ),
          },
          {
            id: 'edit',
            cell: ({ row }) => (
              <Button
                className="place-self-end"
                disabled={!canManage}
                variant="outline"
                onClick={() => {
                  setEditCategory(row.original)
                  setShowModal(true)
                }}
              >
                Edit
              </Button>
            ),
            size: 80,
          },
          {
            id: 'delete',
            cell: ({ row }) => (
              <ConfirmButton
                confirmContent={`Are you sure you want to delete the category: ${row.original.name}?`}
                confirmTitle="Delete Category"
                className="place-self-end"
                disabled={!canManage}
                variant="destructive"
                loading={pendingDelete}
                onClick={() => deleteCategory(row.original)}
              >
                Delete
              </ConfirmButton>
            ),
            size: 80,
          },
        ]}
        data={entityCategories}
        loading={isPending}
        pagination={pagination}
        rowCount={entityCategories.length}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
      />

      {showModal && (
        <AddEditCategoryModal
          entityId={entityId}
          editCategory={editCategory}
          resetAndClose={() => {
            setShowModal(false)
            setEditCategory(null)
            queryClient.invalidateQueries({
              queryKey: ['external-certification-categories', entityId],
            })
          }}
        />
      )}
    </Layout>
  )
})

export default CertificationCategories
